import { RetailerFormType } from '@monolith/legacy/types/retailer-form-type';
import { BrandFormType } from '@monolith/legacy/types/brand-form-type';
import { RetailerSignupSteps } from '@monolith/legacy/types/retailer-signup-steps';
import type { AuthPopinFormType } from '@monolith/legacy/types/auth-popin';
import type { SignupAudience } from '@monolith/legacy/types/signup-audience';

/**
 * Steps each funnel page exclusively owns, derived from its enum so new steps
 * classify automatically. Each funnel page hosts only its own wizard — the shared
 * login/email/code screens are modal-only (opened from a CTA), never an
 * addressable page ?step=, so AuthFormType steps are deliberately absent here.
 */
const OWNED_STEPS: Record<SignupAudience, AuthPopinFormType[]> = {
  retailer: Object.values(RetailerFormType) as AuthPopinFormType[],
  brand: Object.values(BrandFormType) as AuthPopinFormType[],
};

/**
 * Whether a funnel page accepts a requested `?step`: it must be a wizard step the
 * funnel owns. Everything else (modal auth/email steps, the other funnel's steps,
 * unknown hand-crafted strings) falls back to the funnel's own default.
 *
 * Only the retailer page validates through here. The brand landing addresses its own
 * steps by number (see BRAND_FUNNEL_ENTRY_STEP) and resolves them itself, so do not
 * route a brand page step through this.
 */
export function acceptsStep(audience: SignupAudience, step: AuthPopinFormType): boolean {
  return OWNED_STEPS[audience].includes(step);
}

/**
 * First step of the brand landing's own wizard. `brand-register` names an AuthPopin
 * view, not a page step, so sending it as `?step` made the URL claim a step the page
 * does not have — it only ever fell through to step 1. Naming the step the page
 * actually renders keeps the URL honest from the first paint.
 */
export const BRAND_FUNNEL_ENTRY_STEP = '1';

/** Either an AuthPopin view (retailer) or a numbered page step (brand). */
export type FunnelEntryStep = AuthPopinFormType | typeof BRAND_FUNNEL_ENTRY_STEP;

/**
 * Single source of truth for where each audience's registration funnel lands:
 * the page path and the initial step param. Both handoffs into the full-screen
 * funnel — the email-verify redirects and the Google register redirect in
 * use-google-sign-in — route through this so the (path, step) map can't drift.
 */
export function getFunnelEntry(audience: SignupAudience): { path: string; step: FunnelEntryStep } {
  switch (audience) {
    case 'brand':
      return { path: '/brand-registration', step: BRAND_FUNNEL_ENTRY_STEP };
    case 'retailer':
      return { path: '/register', step: RetailerFormType.Register };
  }
}

/**
 * Hard-navigate into an audience's full-screen registration funnel. The single
 * builder shared by both handoffs — the email-verify redirects and the Google
 * register redirect (use-google-sign-in) — so retailer and brand stay in
 * lock-step. Current URL params (utm_*, etc.) are carried over for both
 * audiences; google_* and lift params are included when supplied.
 */
export function redirectToRegistrationFunnel(options: {
  audience: SignupAudience;
  email?: string;
  verificationToken?: string;
  google?: { firstName?: string; lastName?: string; id?: string };
  lift?: { brandId?: string; code?: string };
}): void {
  const { path, step } = getFunnelEntry(options.audience);
  const funnelParams = new URLSearchParams({
    step,
    email: options.email || '',
    ...(options.verificationToken ? { email_verification_token: options.verificationToken } : {}),
    ...(options.google?.firstName ? { google_first_name: options.google.firstName } : {}),
    ...(options.google?.lastName ? { google_last_name: options.google.lastName } : {}),
    ...(options.google?.id ? { google_id: options.google.id } : {}),
    ...(options.lift?.brandId ? { liftBrandId: options.lift.brandId } : {}),
    ...(options.lift?.code ? { liftCode: options.lift.code } : {}),
  });
  const currentParams = new URLSearchParams(window.location.search);
  currentParams.forEach((value, key) => {
    if (!funnelParams.has(key)) {
      funnelParams.append(key, value);
    }
  });
  window.location.href = `${path}?${funnelParams}`;
}

export function getCurrentRegistrationFunnelStep(): RetailerSignupSteps {
  const params = new URLSearchParams(window.location.search);

  return params.get('step') === RetailerFormType.Register
    ? RetailerSignupSteps.FunnelWizard
    : RetailerSignupSteps.EmailOnlySignUp;
}
